import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, RotateCcw } from "lucide-react";
import { mockDoctors } from "../data/mockData";
import { CASES, ROSTER } from "../data/sporeGames";
import { getProgress, resetProgress } from "../lib/progress";
import { HazardTicker, HeaderActions, SporeHeader, SporePage, sporeBtn } from "./SporeShell";
import { ScientistCard } from "./ScientistCard";

const STEPS = ["Pick a scientist", "Beat their test before they move on", "Learn: infected or not?"];

const fmtTime = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${s.toFixed(1)}s`);

const ProgressPanel = ({ done, total, totalTime, infected, onContinue, onReset }) => {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!confirm) return undefined;
    const t = setTimeout(() => setConfirm(false), 4000);
    return () => clearTimeout(t);
  }, [confirm]);
  return (
    <div data-testid="progress-panel" className="rounded-sm border border-slate-800 bg-[#0E131F] p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <p className="font-tac text-xs tracking-[0.25em] text-slate-500">FILES UNSEALED</p>
          <p className="font-display text-5xl font-bold mt-1">
            <span data-testid="progress-count" className="text-[#FF2E63]">{String(done).padStart(2, "0")}</span>
            <span className="text-slate-600 text-3xl"> / {total}</span>
          </p>
        </div>
        <p data-testid="progress-total-time" className="font-tac text-xs text-slate-400 text-right">BEST TIMES TOTAL<br /><span className="text-cyan-300 text-sm">{fmtTime(totalTime)}</span></p>
      </div>
      <div className="grid grid-cols-2 gap-3 font-tac text-xs">
        <p className="border border-red-500/40 bg-red-950/30 text-red-300 px-3 py-2">INFECTED FOUND <span data-testid="progress-infected-count" className="text-lg ml-1">{infected}</span></p>
        <p className="border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 px-3 py-2">NOT INFECTED <span data-testid="progress-clear-count" className="text-lg ml-1">{done - infected}</span></p>
      </div>
      <div className="h-3 bg-slate-950 rounded-full border border-slate-800 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cyan-500 via-amber-500 to-[#FF2E63] transition-[width] duration-500" style={{ width: `${(done / total) * 100}%` }} />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        {done < total && (
          <button type="button" data-testid="continue-btn" onClick={onContinue} className={`${sporeBtn.primary} flex-1 py-3`}>
            <Play className="h-4 w-4" /> {done === 0 ? "Start first file" : "Next sealed file"}
          </button>
        )}
        <button
          type="button"
          data-testid="reset-progress-btn"
          disabled={done === 0}
          onClick={() => (confirm ? (onReset(), setConfirm(false)) : setConfirm(true))}
          onBlur={() => setConfirm(false)}
          className={`${sporeBtn.ghost} py-3 disabled:opacity-40 ${confirm ? "border-red-500 text-red-300" : ""}`}
        >
          <RotateCcw className="h-4 w-4" /> {confirm ? "Tap again to confirm" : "Reset"}
        </button>
      </div>
    </div>
  );
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "sealed", label: "Sealed" },
  { id: "revealed", label: "Unsealed" },
  { id: "infected", label: "Infected" },
  { id: "clear", label: "Not infected" },
];

const matches = (filter, record, entry) => {
  if (filter === "sealed") return !record;
  if (filter === "revealed") return !!record;
  if (filter === "infected") return !!record && entry.infected;
  if (filter === "clear") return !!record && !entry.infected;
  return true;
};

export default function Dashboard({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(getProgress);
  const [filter, setFilter] = useState("all");
  const total = mockDoctors.length;
  const doneIds = Object.keys(progress).filter((id) => CASES[id]);
  const totalTime = doneIds.reduce((sum, id) => sum + progress[id].seconds, 0);
  const infected = doneIds.filter((id) => CASES[id].infected).length;
  const counts = { all: total, sealed: total - doneIds.length, revealed: doneIds.length, infected, clear: doneIds.length - infected };
  const list = ROSTER.filter((d) => matches(filter, progress[d.id], CASES[d.id]));
  const firstSealed = ROSTER.find((d) => !progress[d.id]);

  return (
    <SporePage>
      <SporeHeader>
        <HeaderActions currentUser={currentUser} onLogout={onLogout} />
      </SporeHeader>
      <HazardTicker />
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-12">
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-14 items-start">
          <div className="space-y-6 spore-fade-up">
            <p className="font-tac text-xs tracking-[0.3em] text-cyan-300/80">SPECIALIST ROSTER // {counts.sealed} SEALED CASE FILES</p>
            <h1 className="font-display uppercase font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Infected<br />
              <span className="text-[#FF2E63] spore-glow-red">or not?</span>
            </h1>
            <p className="text-slate-400 text-base max-w-xl leading-relaxed">
              Every scientist holds one symptom and only stays long enough for one test. Some symptoms mean the patient is INFECTED with the spore; others are harmless, and they won&apos;t always be the ones you expect. Beat each mini-game or riddle before the scientist moves on to learn the verdict.
            </p>
            <ol className="flex flex-wrap gap-x-8 gap-y-3">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="font-tac text-[#FF2E63]">0{i + 1}</span> {s}
                </li>
              ))}
            </ol>
          </div>
          <ProgressPanel
            done={doneIds.length}
            total={total}
            totalTime={totalTime}
            infected={infected}
            onContinue={() => navigate(`/doctor/${firstSealed.id}`)}
            onReset={() => {
              resetProgress();
              setProgress({});
            }}
          />
        </section>

        <section className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <h2 className="font-display uppercase font-bold text-lg tracking-wide">Specialists on rotation</h2>
            <div className="flex flex-wrap gap-2" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`font-tac text-xs tracking-widest uppercase px-3 py-1.5 rounded-sm border transition-[background-color,border-color,color] duration-200 ${
                    filter === f.id ? "border-cyan-400 bg-cyan-950/60 text-cyan-200" : "border-slate-800 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {f.label} <span className="text-slate-500">{counts[f.id]}</span>
                </button>
              ))}
            </div>
          </div>
          {list.length === 0 ? (
            <p data-testid="roster-empty" className="font-tac text-sm text-slate-500 py-12 text-center">NO FILES IN THIS VIEW</p>
          ) : (
            <div data-testid="scientist-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {list.map((doctor, i) => (
                <ScientistCard key={doctor.id} doctor={doctor} entry={CASES[doctor.id]} record={progress[doctor.id]} index={i} onOpen={() => navigate(`/doctor/${doctor.id}`)} />
              ))}
            </div>
          )}
        </section>

        <footer className="border-t border-slate-800 pt-6 font-tac text-xs text-slate-600 flex flex-wrap justify-between gap-3">
          <span>PROJECT SP-ORE OVERSIGHT // SPECIAL PROJECTS — OPERATIONAL RISK AND ENVIRONMENT</span>
          <span className="text-red-400/70">KNOW WHICH SIGNS MEAN INFECTION</span>
        </footer>
      </main>
    </SporePage>
  );
}
